/**
 * FiltersView Component
 *
 * This file defines the FiltersView component, which provides a user interface
 * for applying filters through a combination of checkboxes and a date picker.
 * Users can select categories, choose a date, and reset or apply their filter choices.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { View, Text, StyleSheet, ScrollView } from "react-native";
import Checkbox from "../../../components/Checkbox/Checkbox";
import { COLORS, STRINGS } from "../../../constants";
import EventStore from "../../../storage/eventStore";
import { useNavigation } from "@react-navigation/native";
import { ActivityIndicator } from "react-native-paper";
import { useRoute } from "@react-navigation/native";
import { logAnalyticsEvent } from "../../../utils/firebase";

const FiltersView = () => {
  logAnalyticsEvent("screen_view", { name: "VBGo-FiltersView" });

  const categories = EventStore((state) => state.categories);
  const fetchCategories = EventStore((state) => state.fetchCategories);
  const applyFilters = EventStore((state) => state.applyFilters);
  const selectedCategories = EventStore((state) => state.selectedCategories);
  const loadingCategories = EventStore((state) => state.loadingCategories);

  const [localCategories, setLocalCategories] = useState(selectedCategories);

  const navigation = useNavigation();
  const route = useRoute();

  const today = new Date();
  const oneWeekLater = new Date();
  oneWeekLater.setDate(today.getDate() + 6);

  const selectedFromDate = EventStore((state) => state.selectedFromDate);
  const selectedToDate = EventStore((state) => state.selectedToDate);

  const [fromDate, setFromDate] = useState(selectedFromDate ? new Date(selectedFromDate) : null);
  const [toDate, setToDate] = useState(selectedToDate ? new Date(selectedToDate) : null);

  /**
   * Effect: Sync local selections to route params so the in-app back button can read them.
   */
  useEffect(() => {
    navigation.setParams({ localCategories });
  }, [localCategories]);

  /**
   * handleCheckboxChange
   *
   * Toggles selected categories in the store.
   */
  const handleCheckboxChange = useCallback((category) => {
    setLocalCategories((prev) => (prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]));
  }, []);

  /**
   * handleReset
   *
   * Resets filters.
   */
  const handleReset = () => {
    EventStore.setState({ selectedCategories: [] });
    setFromDate("");
    setToDate("");
  };

  /**
   * handleFilterResults
   *
   * Updates Zustand store with selected filters, writes categories to URL,
   * and applies them. Wrapped in useCallback so the beforeRemove listener
   * always closes over the current localCategories (no stale closure).
   */
  const handleFilterResults = useCallback(async () => {
    EventStore.setState({
      selectedCategories: localCategories,
      selectedFromDate: fromDate || "",
      selectedToDate: toDate || "",
    });

    // Navigate to ListView passing categories as a route param.
    // React Navigation will serialize this into the URL via the linking config.
    navigation.navigate("ListView", {
      category: localCategories.length > 0 ? localCategories : undefined,
    });

    await applyFilters();
  }, [localCategories, fromDate, toDate, applyFilters, navigation]);

  /**
   * Effect: Detect "Clear All" action — clears only local state (categories
   * and dates) without modifying the store or navigating away.  The store is
   * only committed when the user explicitly navigates back via the back button.
   */
  useEffect(() => {
    if (route.params?.clearFilters) {
      setLocalCategories([]);
      setFromDate("");
      setToDate("");
      navigation.setParams({ clearFilters: false });
    }
  }, [route.params?.clearFilters]);

  /**
   * Effect: Fetches Categories List on mount
   */
  useEffect(() => {
    fetchCategories();
  }, []);

  const renderCategories = useMemo(() => {
    return categories.map((category) => (
      <View key={category.name} style={styles.checkboxContainer}>
        <Checkbox
          label={category.name}
          value={localCategories.includes(category.name)}
          onChange={() => handleCheckboxChange(category.name)}
          color={COLORS.primary}
          uncheckedColor={COLORS.border}
        />
      </View>
    ));
  }, [localCategories, categories]);

  return (
    <ScrollView
      style={styles.screen}
      contentContainerStyle={styles.scrollContainer}
      keyboardShouldPersistTaps="handled"
    >
      {/* Date Picker */}
      {/* <View style={styles.datePickerContainer}>
        <Text style={styles.datePickerLabel}>{STRINGS.datePickerLabel}</Text>
        <DatePicker
          fromDate={!fromDate || isNaN(new Date(fromDate).getTime()) ? null : new Date(fromDate)}
          toDate={!toDate || isNaN(new Date(toDate).getTime()) ? null : new Date(toDate)}
          onFromDateChange={(date) => {
            setFromDate(date);
            resetVerify(true);
          }}
          onToDateChange={(date) => {
            setToDate(date);
            resetVerify(true);
          }}
        />
      </View> */}

      {/* Category Selection */}
      <View style={styles.container}>
        <Text style={styles.heading}>{STRINGS.category}</Text>

        {loadingCategories ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator animating={true} size="small" color={COLORS.primary} />
            <Text style={styles.loadingText}>{STRINGS.loadingCategories}</Text>
          </View>
        ) : categories.length === 0 ? (
          <View style={styles.noCategoriesContainer}>
            <Text style={styles.noCategoriesText}>{STRINGS.noCategories}</Text>
          </View>
        ) : (
          renderCategories
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: COLORS.backgroundColor,
  },
  scrollContainer: {
    flexGrow: 1,
    paddingHorizontal: 16,
    paddingBottom: 150,
    alignItems: "stretch",
  },
  datePickerContainer: {
    alignSelf: "flex-start",
    marginTop: 24,
    marginBottom: 12,
  },
  datePickerLabel: {
    fontSize: 16,
    fontWeight: "600",
    color: COLORS.textDarker,
    marginBottom: 16,
    textAlign: "left",
  },
  container: {
    width: "100%",
    marginTop: 16,
  },
  heading: {
    fontSize: 16,
    marginBottom: 16,
    fontWeight: "600",
    color: COLORS.textDarker,
    textAlign: "left",
  },
  checkboxContainer: {
    marginBottom: 12,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: COLORS.backgroundColor,
    paddingTop: 16,
    paddingBottom: 32,
    gap: 16,
  },
  loadingContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
  },
  loadingText: {
    fontSize: 14,
    color: COLORS.textDark,
  },
  noCategoriesContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
  },
  noCategoriesText: {
    fontSize: 14,
    color: COLORS.textDark,
  },
});

export default FiltersView;
